import { ExternalLinkIcon, GitPullRequestIcon, RefreshCwIcon } from "lucide-react";

import { Button } from "../ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../ui/empty";

/**
 * Why a surface is showing no pull requests. Every caller that cannot read them renders this, so
 * an Environment without the capability, a failed read and a missing credential all read the
 * same way rather than each inventing their own empty screen.
 */
export function PullRequestsUnavailableState({
  title = "Could not load Pull Requests",
  error,
  onRetry,
  hostUrl,
  role = "status",
}: {
  readonly title?: string;
  /** The caller names the fix — update the Environment, sign in — because only it knows which. */
  readonly error: string;
  readonly onRetry?: (() => void) | undefined;
  /** Where the reader can go when this Environment cannot serve it. */
  readonly hostUrl?: string | undefined;
  /** A failed read is an alert; an Environment that simply cannot is a status. */
  readonly role?: "status" | "alert";
}) {
  return (
    <Empty
      className="min-h-0 justify-center-safe overflow-y-auto px-4 py-16 md:px-4 [&>*]:shrink-0"
      role={role}
    >
      <EmptyMedia variant="icon">
        <GitPullRequestIcon />
      </EmptyMedia>
      <EmptyHeader>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{error}</EmptyDescription>
      </EmptyHeader>
      {onRetry === undefined && hostUrl === undefined ? null : (
        <EmptyContent className="flex-row flex-wrap justify-center gap-2">
          {onRetry === undefined ? null : (
            <Button size="sm" variant="outline" onClick={onRetry}>
              <RefreshCwIcon aria-hidden className="size-3.5" />
              Retry
            </Button>
          )}
          {hostUrl === undefined ? null : (
            <Button
              size="sm"
              variant="outline"
              render={<a href={hostUrl} target="_blank" rel="noopener noreferrer" />}
            >
              <ExternalLinkIcon aria-hidden className="size-3.5" />
              Open on GitHub
            </Button>
          )}
        </EmptyContent>
      )}
    </Empty>
  );
}
